import { supabase } from './supabase'
import type { AskIntentKey } from './wiki'

/**
 * Ask One on the desk — 126, HLP-35. The client half of `#/support/ask-one`.
 *
 * ── WHAT THIS READS, AND WHAT IT NEVER CAN ──────────────────────────────────
 *
 * Two logs that carry NO PERSON and a DATE, not a timestamp (037, 048): the
 * first turn of each conversation (`wiki_query_log`) and the thumbs
 * (`wiki_feedback`). Nothing here can name who asked, because nothing it reads
 * knows. The transcripts (HLP-13) are self-read-only and are not read here, by
 * anybody, ever.
 *
 * ── WHO ─────────────────────────────────────────────────────────────────────
 *
 * `analytics.read` — comms admins, and the tenant admin since 126. Every read
 * REFUSES anybody else (rule 12), so an error from here is a sentence to put on
 * the screen, never an empty dashboard.
 *
 * ── THE SERVER SORTS, FILTERS AND LIMITS (standing rule 1) ──────────────────
 *
 * `ask_one_questions` takes the filter, theme and search and applies them
 * before the limit, and carries the honest total from the same statement. The
 * route never sorts or filters the rows it is handed. The summary's `daily`
 * series is every day of the period (no limit), so the tiles are arithmetic
 * over rows the caller already holds — rule 1's stated exception.
 */

export type AskOneDays = 7 | 30

export type AskOneFilter = 'action' | 'none' | 'inferred' | 'down' | 'direct' | 'done' | 'all'

/** The chips, in the order they render. The first is the default view. */
export const ASK_ONE_FILTERS: ReadonlyArray<{ key: AskOneFilter; label: string }> = [
  { key: 'action', label: 'Needs a look' },
  { key: 'none', label: 'Nobody could answer' },
  { key: 'inferred', label: 'One inferred' },
  { key: 'down', label: 'Rated wrong' },
  { key: 'direct', label: 'Answered' },
  { key: 'done', label: 'Reviewed or dismissed' },
  { key: 'all', label: 'Everything' },
]

export type AskOneCorpus = 'wiki' | 'platform' | 'both' | 'none'
export type AskOneOutcome = 'board' | 'platform' | 'hr' | 'technical'

export type AskOneDay = { day: string; direct: number; inferred: number; none: number; unavailable: number }

export type AskOneSummary = {
  days: AskOneDays
  from: string
  to: string
  daily: AskOneDay[]
  ratings: { up: number; down: number }
  /** Answered turns by guide. `unrecorded` = before 126 started logging it. */
  corpus: Record<string, number>
  /** Declared intents. `none` = the person just typed. */
  intent: Record<string, number>
  /** By basis, where people went next. `unreported` = nothing was reported — NEVER "satisfied". */
  outcome: Array<{ basis: 'direct' | 'inferred' | 'none'; outcome: AskOneOutcome | 'unreported'; n: number }>
  /** The first day corpus was recorded, or null if never. The panels say so. */
  recordedSince: string | null
  /** Private questions and board posts in the same period. NOT linked to any conversation, by design. */
  sent: { board: number; platform: number; technical: number; hr: number }
}

export type AskOneReviewState = 'reviewed' | 'dismissed' | 'drafted' | 'sent'
export type AskOneReason = 'covered' | 'watching' | 'passed_on' | 'not_ours' | 'noise' | 'duplicate'

export const REVIEW_REASONS: Record<'reviewed' | 'dismissed', ReadonlyArray<{ key: AskOneReason; label: string }>> = {
  reviewed: [
    { key: 'covered', label: 'Covered by an answer now' },
    { key: 'watching', label: 'Worth watching' },
    { key: 'passed_on', label: 'Passed to a team' },
  ],
  dismissed: [
    { key: 'not_ours', label: 'Not ours to answer (HR or technical)' },
    { key: 'noise', label: 'Test or noise' },
    { key: 'duplicate', label: 'Duplicate of another line' },
  ],
}

export function reasonLabel(reason: AskOneReason | null): string | null {
  if (!reason) return null
  for (const list of Object.values(REVIEW_REASONS)) {
    const hit = list.find((r) => r.key === reason)
    if (hit) return hit.label
  }
  return reason
}

export type AskOneQuestion = {
  /** app.wiki_fold_query() of the question — the one key (rule 11). */
  foldKey: string
  /** The most recent wording somebody typed. */
  sample: string
  /** First turns that asked it. ZERO means it was only ever RATED, on a follow-up (126b). */
  times: number
  firstAsked: string
  lastAsked: string
  nDirect: number
  nInferred: number
  nNone: number
  /** The model call failed. An outage, not a gap. */
  nUnavailable: number
  corpora: AskOneCorpus[]
  outcomes: AskOneOutcome[]
  ups: number
  downs: number
  downReasons: string[]
  /** The latest answer somebody rated wrong. Shown behind a disclosure — 048's one exception. */
  ratedAnswer: string | null
  reviewState: AskOneReviewState | null
  reviewReason: AskOneReason | null
  reviewedOn: string | null
  /** Asked again after it was reviewed — derived by the read, never stored (rule 8). */
  askedAgain: boolean
  draftContentId: string | null
  draftPublished: boolean
  trackerIssueId: string | null
  trackerStatus: string | null
  themeId: string | null
  themeTitle: string | null
}

export type AskOnePage = { rows: AskOneQuestion[]; total: number }

export type AskOneTheme = {
  id: string
  rank: number
  title: string
  summary: string
  gap: string | null
  suggestedAction: 'draft' | 'developers' | 'review' | 'dismiss' | 'none'
  /** Counted by the DATABASE from the log, not claimed by the model. */
  asks: number
  nDirect: number
  nInferred: number
  nNone: number
  model: string
  writtenAt: string
}

export const THEME_ACTIONS: Record<AskOneTheme['suggestedAction'], string> = {
  draft: 'Draft a Wiki One answer',
  developers: 'Send to developers',
  review: 'Review',
  dismiss: 'Dismiss',
  none: 'Nothing to do yet',
}

type Row = Record<string, unknown>
const num = (v: unknown) => Number(v ?? 0)
const str = (v: unknown) => (v == null ? null : String(v))
const arr = <T>(v: unknown) => (Array.isArray(v) ? (v as T[]) : [])

export async function loadAskOneSummary(days: AskOneDays): Promise<AskOneSummary> {
  const { data, error } = await supabase.rpc('ask_one_summary', { p_days: days })
  if (error) throw new Error(error.message)
  const d = (data ?? {}) as Row
  const sent = (d.sent ?? {}) as Row
  const ratings = (d.ratings ?? {}) as Row
  return {
    days: num(d.days) === 7 ? 7 : 30,
    from: String(d.from ?? ''),
    to: String(d.to ?? ''),
    daily: arr<Row>(d.daily).map((x) => ({
      day: String(x.day),
      direct: num(x.direct),
      inferred: num(x.inferred),
      none: num(x.none),
      unavailable: num(x.unavailable),
    })),
    ratings: { up: num(ratings.up), down: num(ratings.down) },
    corpus: (d.corpus ?? {}) as Record<string, number>,
    intent: (d.intent ?? {}) as Record<string, number>,
    outcome: arr<Row>(d.outcome).map((x) => ({
      basis: x.basis as AskOneSummary['outcome'][number]['basis'],
      outcome: x.outcome as AskOneSummary['outcome'][number]['outcome'],
      n: num(x.n),
    })),
    recordedSince: str(d.recorded_since),
    sent: { board: num(sent.board), platform: num(sent.platform), technical: num(sent.technical), hr: num(sent.hr) },
  }
}

export async function loadAskOneQuestions(opts: {
  days: AskOneDays
  filter: AskOneFilter
  themeId?: string | null
  search?: string
  limit?: number
}): Promise<AskOnePage> {
  const { data, error } = await supabase.rpc('ask_one_questions', {
    p_days: opts.days,
    p_filter: opts.filter,
    p_theme: opts.themeId ?? null,
    p_search: opts.search?.trim() ? opts.search.trim() : null,
    p_limit: opts.limit ?? 25,
  })
  if (error) throw new Error(error.message)
  const rows = ((data ?? []) as Row[]).map(questionFrom)
  const total = rows.length ? num(((data as Row[])[0] as Row).total) : 0
  return { rows, total }
}

export function questionFrom(r: Row): AskOneQuestion {
  return {
    foldKey: String(r.fold_key),
    sample: String(r.sample ?? r.fold_key),
    times: num(r.times),
    firstAsked: String(r.first_asked ?? ''),
    lastAsked: String(r.last_asked ?? ''),
    nDirect: num(r.n_direct),
    nInferred: num(r.n_inferred),
    nNone: num(r.n_none),
    nUnavailable: num(r.n_unavailable),
    corpora: arr<AskOneCorpus>(r.corpora),
    outcomes: arr<AskOneOutcome>(r.outcomes),
    ups: num(r.ups),
    downs: num(r.downs),
    downReasons: arr<string>(r.down_reasons),
    ratedAnswer: str(r.rated_answer),
    reviewState: (r.review_state as AskOneReviewState) ?? null,
    reviewReason: (r.review_reason as AskOneReason) ?? null,
    reviewedOn: str(r.reviewed_on),
    askedAgain: Boolean(r.asked_again),
    draftContentId: str(r.draft_content_id),
    draftPublished: Boolean(r.draft_published),
    trackerIssueId: str(r.tracker_issue_id),
    trackerStatus: str(r.tracker_status),
    themeId: str(r.theme_id),
    themeTitle: str(r.theme_title),
  }
}

/** The chips' counts — the whole period, never the page (118's shape). */
export async function loadAskOneCounts(days: AskOneDays, themeId: string | null): Promise<Record<AskOneFilter, number>> {
  const { data, error } = await supabase.rpc('ask_one_question_counts', { p_days: days, p_theme: themeId })
  if (error) throw new Error(error.message)
  const out = { action: 0, none: 0, inferred: 0, down: 0, direct: 0, done: 0, all: 0 } as Record<AskOneFilter, number>
  for (const r of (data ?? []) as Row[]) out[r.filter as AskOneFilter] = num(r.n)
  return out
}

export async function loadAskOneThemes(days: AskOneDays): Promise<AskOneTheme[]> {
  const { data, error } = await supabase.rpc('ask_one_themes', { p_days: days })
  if (error) throw new Error(error.message)
  return ((data ?? []) as Row[]).map((t) => ({
    id: String(t.id),
    rank: num(t.rank),
    title: String(t.title),
    summary: String(t.summary),
    gap: str(t.gap),
    suggestedAction: (t.suggested_action as AskOneTheme['suggestedAction']) ?? 'none',
    asks: num(t.asks),
    nDirect: num(t.n_direct),
    nInferred: num(t.n_inferred),
    nNone: num(t.n_none),
    model: String(t.model ?? ''),
    writtenAt: String(t.written_at ?? ''),
  }))
}

// ── the four acts ────────────────────────────────────────────────────────────
// Each is a definer function that checks analytics.read (and, for the draft,
// content.draft) itself — rule 21. Named arguments throughout (071).

export async function reviewAskOne(foldKey: string, state: 'reviewed' | 'dismissed', reason: AskOneReason): Promise<void> {
  const { error } = await supabase.rpc('review_ask_one', { p_fold_key: foldKey, p_state: state, p_reason: reason })
  if (error) throw new Error(error.message)
}

export async function reopenAskOne(foldKey: string): Promise<void> {
  const { error } = await supabase.rpc('reopen_ask_one', { p_fold_key: foldKey })
  if (error) throw new Error(error.message)
}

export async function draftWikiAnswerFromAsk(foldKey: string, question: string, answer: string): Promise<string> {
  const { data, error } = await supabase.rpc('draft_wiki_answer_from_ask', {
    p_fold_key: foldKey,
    p_question: question,
    p_answer: answer,
  })
  if (error) throw new Error(error.message)
  return String(data)
}

export async function sendAskOneToDevelopers(foldKey: string, title: string, body: string): Promise<void> {
  const { error } = await supabase.rpc('send_ask_one_to_developers', { p_fold_key: foldKey, p_title: title, p_body: body })
  if (error) throw new Error(error.message)
}

/**
 * 126. Intent and outcome, after the answer — the drawer's half.
 *
 * A SEPARATE WRITE FROM THE MODEL CALL, which is the whole point: HLP-16 made
 * it a type-level rule that the intent never reaches `askOne`, and this is how
 * the log learns it without that rule bending. Write-once per column on the
 * server; the row names nobody.
 *
 * NEVER THROWS AND IS NEVER AWAITED BY A SEND. A stamp that fails costs one
 * line on a dashboard; a send that failed because of it would cost somebody
 * their question.
 */
export async function stampAskOne(
  logRef: string | null | undefined,
  stamp: { intent?: AskIntentKey | null; outcome?: AskOneOutcome | null },
): Promise<void> {
  if (!logRef) return
  const intent = stamp.intent ?? null
  const outcome = stamp.outcome ?? null
  if (!intent && !outcome) return
  try {
    await supabase.rpc('stamp_ask_one', { p_ref: logRef, p_intent: intent, p_outcome: outcome })
  } catch {
    /* see above */
  }
}

/** Words for the screen. Staff screen, English on purpose (like the curator and admin screens). */
export const OUTCOME_WORDS: Record<AskOneOutcome | 'unreported', string> = {
  board: 'Posted to the board',
  platform: 'Platform support',
  hr: 'HR support',
  technical: 'Technical expert',
  unreported: 'Nothing reported',
}

export const CORPUS_WORDS: Record<string, string> = {
  wiki: 'Wiki One',
  platform: 'Platform guide',
  both: 'Both',
  none: 'Neither',
  unrecorded: 'Not recorded (before 126)',
}

export function dayLabel(iso: string): string {
  if (!iso) return ''
  const d = new Date(`${iso.slice(0, 10)}T12:00:00Z`)
  return d.toLocaleDateString('en-CA', { day: 'numeric', month: 'short', timeZone: 'UTC' })
}

/** "8 Sep" or "8 – 11 Sep". */
export function spanLabel(first: string, last: string): string {
  return first === last || !first ? dayLabel(last) : `${dayLabel(first)} – ${dayLabel(last)}`
}

export type AskOneTotals = { conversations: number; direct: number; inferred: number; none: number; unavailable: number }

/** Arithmetic over the daily series the caller holds whole (rule 1's exception). */
export function totalsOf(daily: readonly AskOneDay[]): AskOneTotals {
  const t = { conversations: 0, direct: 0, inferred: 0, none: 0, unavailable: 0 }
  for (const d of daily) {
    t.direct += d.direct
    t.inferred += d.inferred
    t.none += d.none
    t.unavailable += d.unavailable
  }
  t.conversations = t.direct + t.inferred + t.none + t.unavailable
  return t
}

export function pct(n: number, of: number): string {
  return of > 0 ? `${Math.round((n * 100) / of)}%` : '–'
}
